import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  Description as DescriptionIcon,
  TableChart as TableChartIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const Backup = () => {
  const [filterType, setFilterType] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportFormat, setExportFormat] = useState('excel');
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (filterType === 'custom' && (!startDate || !endDate)) {
      toast.error('Please select both start and end dates for custom range');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      // Build query parameters
      const params = {
        format: exportFormat,
        filter: filterType,
      };

      if (filterType === 'day') {
        params.date = dateFilter || format(new Date(), 'yyyy-MM-dd');
      } else if (filterType === 'month') {
        params.month = dateFilter || format(new Date(), 'yyyy-MM');
      } else if (filterType === 'year') {
        params.year = dateFilter || format(new Date(), 'yyyy');
      } else if (filterType === 'custom') {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      const response = await axios.get(`${API_BASE_URL}/smartbills/export`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      });

      // Determine file extension and MIME type
      let extension = '';
      let mimeType = '';
      switch (exportFormat) {
        case 'sql':
          extension = 'sql';
          mimeType = 'application/sql';
          break;
        case 'excel':
          extension = 'xlsx';
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'pdf':
          extension = 'pdf';
          mimeType = 'application/pdf';
          break;
        default:
          extension = 'txt';
          mimeType = 'text/plain';
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const timestamp = format(new Date(), 'yyyyMMdd_HHmmss');
      let filename = `smart_bills_backup_${timestamp}`;
      if (filterType !== 'all') {
        filename += `_${filterType}`;
      }
      filename += `.${extension}`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Backup downloaded successfully as ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.response?.data?.message || 'Failed to download backup');
    } finally {
      setLoading(false);
    }
  };

  const getFilterLabel = () => {
    switch (filterType) {
      case 'day':
        return 'Day';
      case 'month':
        return 'Month';
      case 'year':
        return 'Year';
      case 'custom':
        return 'Custom Range';
      default:
        return 'All Bills';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Backup & Export
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Export Smart Bills Data
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Download your smart bills data in various formats. Select a date range and export format.
          </Typography>

          <Grid container spacing={3}>
            {/* Filter Type Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Filter Type
                </FormLabel>
                <RadioGroup
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <FormControlLabel value="all" control={<Radio />} label="All Bills" />
                  <FormControlLabel value="day" control={<Radio />} label="By Day" />
                  <FormControlLabel value="month" control={<Radio />} label="By Month" />
                  <FormControlLabel value="year" control={<Radio />} label="By Year" />
                  <FormControlLabel value="custom" control={<Radio />} label="Custom Range" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Date Inputs Based on Filter Type */}
            <Grid item xs={12} md={6}>
              {filterType === 'day' && (
                <TextField
                  fullWidth
                  label="Select Date"
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  defaultValue={format(new Date(), 'yyyy-MM-dd')}
                />
              )}
              {filterType === 'month' && (
                <TextField
                  fullWidth
                  label="Select Month"
                  type="month"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  defaultValue={format(new Date(), 'yyyy-MM')}
                />
              )}
              {filterType === 'year' && (
                <TextField
                  fullWidth
                  label="Select Year"
                  type="number"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 2020, max: 2100 }}
                  defaultValue={format(new Date(), 'yyyy')}
                />
              )}
              {filterType === 'custom' && (
                <Box>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
            </Grid>

            {/* Export Format Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <FormLabel component="legend" sx={{ mb: 1 }}>
                  Export Format
                </FormLabel>
                <RadioGroup
                  row
                  value={exportFormat}
                  onChange={(e) => setExportFormat(e.target.value)}
                >
                  <FormControlLabel
                    value="excel"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableChartIcon />
                        <span>Excel (.xlsx)</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="sql"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon />
                        <span>SQL (.sql)</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="pdf"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PdfIcon />
                        <span>PDF (.pdf)</span>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Info Alert */}
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Export Details:</strong> You are exporting <strong>{getFilterLabel()}</strong> data in{' '}
                  <strong>{exportFormat.toUpperCase()}</strong> format.
                  {filterType === 'custom' && startDate && endDate && (
                    <> From <strong>{startDate}</strong> to <strong>{endDate}</strong></>
                  )}
                </Typography>
              </Alert>
            </Grid>

            {/* Download Button */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={loading || (filterType === 'custom' && (!startDate || !endDate))}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {loading ? 'Preparing Download...' : `Download ${exportFormat.toUpperCase()} Backup`}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Backup;
























